{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://davisjam.github.io/model-based-agentic-software-engineering/workbench/mage-model.schema.json",
  "title": "MAGE model system",
  "description": "A model system: one identity namespace of entities, zero or more graph models, zero or more state machines. Shape only -- meaning is fixed by SEMANTICS.md, whose validation rules V1-V31 this schema cannot all express. Rules marked ENGINE below require a validator pass beyond JSON Schema.",
  "type": "object",
  "required": ["mage", "system"],
  "additionalProperties": false,
  "properties": {
    "mage": {
      "description": "Format version. Incompatible changes bump it.",
      "const": 1
    },
    "system": {
      "type": "object",
      "required": ["id"],
      "additionalProperties": false,
      "properties": {
        "id": { "$ref": "#/$defs/id" },
        "name": { "type": "string" },
        "description": { "type": "string" }
      }
    },

    "domains": {
      "description": "Named value domains. ordered-enum admits <, >, <=, >= (V20).",
      "type": "object",
      "propertyNames": { "$ref": "#/$defs/id" },
      "additionalProperties": {
        "type": "object",
        "required": ["type"],
        "additionalProperties": false,
        "properties": {
          "type": { "enum": ["enum", "ordered-enum", "boolean", "integer"] },
          "values": {
            "description": "Required for enum / ordered-enum. For ordered-enum the list order IS the order, ascending.",
            "type": "array",
            "minItems": 1,
            "items": { "type": "string" }
          },
          "range": { "$ref": "#/$defs/intRange" },
          "description": { "type": "string" }
        },
        "allOf": [
          {
            "if": { "properties": { "type": { "enum": ["enum", "ordered-enum"] } } },
            "then": { "required": ["values"] }
          },
          {
            "if": { "properties": { "type": { "const": "integer" } } },
            "then": { "required": ["range"] }
          }
        ]
      }
    },

    "entity-types": {
      "description": "Optional vocabulary of declared entity-type names (SEMANTICS.md 3.3). Gives a domain/range declaration a resolvable target before any entity carries the kind (V47), and gives 'naming a semantic equivalence class' an authored home. A vocabulary, not a taxonomy: no subtyping, and an entity's type: is not required to appear here.",
      "type": "object",
      "propertyNames": { "$ref": "#/$defs/id" },
      "additionalProperties": {
        "type": "object",
        "required": ["description"],
        "additionalProperties": false,
        "properties": {
          "description": {
            "description": "What membership in this kind asserts. For humans.",
            "type": "string"
          }
        }
      }
    },

    "relation-types": {
      "type": "object",
      "propertyNames": { "$ref": "#/$defs/id" },
      "additionalProperties": {
        "type": "object",
        "required": ["description", "composition"],
        "additionalProperties": false,
        "properties": {
          "description": {
            "description": "What the presence of this relation asserts. For humans.",
            "type": "string"
          },
          "absence": {
            "description": "What the ABSENCE of this relation asserts -- often the more important half. For humans.",
            "type": "string"
          },
          "composition": {
            "type": "object",
            "required": ["path"],
            "additionalProperties": false,
            "properties": {
              "path": {
                "description": "allowed: multi-hop queries over this relation are licensed (transitive closure is a legitimate object of inquiry). forbidden: the engine REFUSES such queries (V7). This is not a claim that the relation itself is transitive.",
                "enum": ["allowed", "forbidden"]
              }
            }
          },
          "aggregates": {
            "description": "Declares that an AUTHORED property on the source is the aggregate of a property read off each target (V45, V46). v0.1 computes nothing over a relation, so the value stays written on the entity; this declaration is what lets the validator reject a disagreement instead of leaving the edge layer decorative.",
            "type": "object",
            "required": ["declared", "over", "using"],
            "additionalProperties": false,
            "properties": {
              "declared": {
                "description": "The property on the SOURCE entity carrying the authored aggregate.",
                "$ref": "#/$defs/id"
              },
              "over": {
                "description": "The property read off each TARGET entity and aggregated.",
                "$ref": "#/$defs/id"
              },
              "using": {
                "description": "The aggregation operator. A closed set of one: a permissive vocabulary cannot be narrowed again without breaking every model that relied on it.",
                "enum": ["max"]
              }
            }
          },
          "domain": {
            "description": "The entity-type name(s) allowed at this relation type's SOURCE (V47, V48). One name or a list; a list means membership in the union. Omitting the key constrains nothing and licenses nothing -- it never asserts that anything may sit here.",
            "$ref": "#/$defs/endpointTypes"
          },
          "range": {
            "description": "The entity-type name(s) allowed at this relation type's TARGET (V47, V48). Same semantics as domain.",
            "$ref": "#/$defs/endpointTypes"
          },
          "properties": {
            "type": "object",
            "additionalProperties": false,
            "properties": {
              "symmetric": { "type": "boolean", "default": false },
              "acyclic": {
                "description": "true makes a cycle in this relation type a validation error (V8).",
                "type": "boolean",
                "default": false
              }
            }
          }
        }
      }
    },

    "entities": {
      "description": "THE identity namespace. Ids are immutable (V2); labels are not.",
      "type": "object",
      "propertyNames": { "$ref": "#/$defs/id" },
      "additionalProperties": {
        "type": "object",
        "additionalProperties": false,
        "properties": {
          "type": { "type": "string" },
          "label": { "type": "string" },
          "description": { "type": "string" },
          "properties": {
            "description": "IMMUTABLE descriptive facts. Never part of the state vector (V16).",
            "$ref": "#/$defs/propertyBag"
          },
          "contains": {
            "description": "Containment. Acyclic (V4) and single-parent (V5); ENGINE-checked.",
            "type": "array",
            "items": { "$ref": "#/$defs/id" }
          },
          "notes": { "$ref": "#/$defs/notes" },
          "provenance": { "$ref": "#/$defs/provenance" }
        }
      }
    },

    "events": {
      "description": "Declared synchronized events (tier T3). An event is ONE system transition involving all participants, applied atomically.",
      "type": "object",
      "propertyNames": { "$ref": "#/$defs/id" },
      "additionalProperties": {
        "type": "object",
        "required": ["participants"],
        "additionalProperties": false,
        "properties": {
          "participants": {
            "description": "Machine names. Every participant must offer a sync:-transition for this event (V12). A multiply-instantiated participant is REFUSED in v0.1 (V14).",
            "type": "array",
            "minItems": 2,
            "items": { "$ref": "#/$defs/id" }
          },
          "description": { "type": "string" }
        }
      }
    },

    "machines": {
      "description": "Behavioral units. One instance unless instances: is declared. A machine need not correspond to an entity; where it does, say so with entity:.",
      "type": "object",
      "propertyNames": { "$ref": "#/$defs/id" },
      "additionalProperties": {
        "type": "object",
        "required": ["initial", "states", "transitions"],
        "additionalProperties": false,
        "properties": {
          "label": {
            "description": "Display name, defaulting to the id. Display only: it never addresses the machine (transactions and selectors use the id) and never enters the transaction base.",
            "type": "string"
          },
          "description": {
            "description": "'Model in words' -- optional, deliberately-authored natural language beside the formal representation. Stored model data with the epistemic status of a code comment: the author or the agent writes it, the human surface renders it verbatim, and no surface generates one when it is absent. Prose, so it stays out of the canonical hash like a label.",
            "type": "string"
          },
          "entity": {
            "description": "Optional correspondence to a system entity (V6).",
            "$ref": "#/$defs/id"
          },
          "instances": {
            "description": "N independent interleaved copies, addressed name[0..N-1]. Default 1, addressed by bare name. Gives occupancy, NOT binding (V14).",
            "type": "integer",
            "minimum": 1,
            "default": 1
          },
          "purpose": { "$ref": "#/$defs/purpose" },
          "initial": { "$ref": "#/$defs/id" },
          "states": {
            "type": "object",
            "minProperties": 1,
            "propertyNames": { "$ref": "#/$defs/id" },
            "additionalProperties": {
              "oneOf": [
                { "type": "null" },
                {
                  "type": "object",
                  "additionalProperties": false,
                  "properties": {
                    "label": { "type": "string" },
                    "description": { "type": "string" }
                  }
                }
              ]
            }
          },
          "variables": {
            "description": "THE state vector. Every variable must have a FINITE domain (V17).",
            "type": "object",
            "propertyNames": { "$ref": "#/$defs/id" },
            "additionalProperties": { "$ref": "#/$defs/variable" }
          },
          "derived": {
            "description": "Pure expressions over current state. Never stored, never part of state identity (V18); dependency graph must be acyclic (V19).",
            "type": "object",
            "propertyNames": { "$ref": "#/$defs/id" },
            "additionalProperties": { "type": "string" }
          },
          "transitions": {
            "type": "array",
            "items": { "$ref": "#/$defs/transition" }
          }
        }
      }
    },

    "models": {
      "description": "Purposeful reductions over the entity namespace.",
      "type": "object",
      "propertyNames": { "$ref": "#/$defs/id" },
      "additionalProperties": {
        "type": "object",
        "required": ["type"],
        "additionalProperties": false,
        "properties": {
          "type": { "const": "graph" },
          "label": { "type": "string" },
          "description": {
            "description": "'Model in words' -- optional, deliberately-authored natural language beside the formal representation. Stored model data with the epistemic status of a code comment: the author or the agent writes it, the human surface renders it verbatim, and no surface generates one when it is absent. Prose, so it stays out of the canonical hash like a label.",
            "type": "string"
          },
          "purpose": { "$ref": "#/$defs/purpose" },
          "entities": {
            "description": "Ids of system-level entities this model includes. Models reference; they never redeclare (V3).",
            "type": "array",
            "items": { "$ref": "#/$defs/id" }
          },
          "notes": { "$ref": "#/$defs/notes" },
          "provenance": { "$ref": "#/$defs/provenance" },
          "relations": {
            "type": "array",
            "items": {
              "type": "object",
              "required": ["from", "to", "type"],
              "additionalProperties": false,
              "properties": {
                "id": { "$ref": "#/$defs/id" },
                "from": { "$ref": "#/$defs/id" },
                "to": { "$ref": "#/$defs/id" },
                "type": { "$ref": "#/$defs/id" },
                "label": { "type": "string" },
                "properties": { "$ref": "#/$defs/propertyBag" },
                "notes": { "$ref": "#/$defs/notes" },
                "provenance": { "$ref": "#/$defs/provenance" }
              }
            }
          }
        }
      }
    },

    "quantities": {
      "description": "Quantitative annotations over existing semantic objects. NEVER part of the behavioral state vector (V6/SEMANTICS.md 5.2) -- a real-valued annotation in the state vector would make the reachable space infinite. ENGINE-checked: targets resolve (V27), dimension and literals are readable (V28), magnitudes are in bounds (V29), dimensions agree (V30), each one reaches an analysis (V36, V37).",
      "type": "object",
      "propertyNames": { "$ref": "#/$defs/id" },
      "additionalProperties": { "$ref": "#/$defs/quantity" }
    },
    "accounting": {
      "description": "How each path-aggregated metric charges its annotations, keyed by metric name (`latency`, `cost`). One basis per metric; the vocabulary is `entities` for v0.1, closed so that adding transition or relation accounting is a deliberate act. Declared per quantitative model -- v0.1 has one per system, the quantities: map, so this is a top-level block. ENGINE-checked (V35): the metric and the basis are known, and a metric with annotations declares one. NEITHER the metric name nor the basis is enumerated here, for the reason `dimension` is not: a wrong one reaches the MEANING layer and both implementations cite V35 instead of Python answering SCHEMA where TypeScript answers a V-number.",
      "type": "object",
      "additionalProperties": { "type": "object" }
    },

    "queries": {
      "description": "Saved queries, re-run when the model changes. Each value conforms to mage-query.schema.json.",
      "type": "object",
      "propertyNames": { "$ref": "#/$defs/id" },
      "additionalProperties": { "type": "object" }
    },

    "requirements": {
      "description": "Obligations the system under design is judged against, keyed by id. A requirement references the POSITIVE breach query by id and declares the proposition value that discharges it (`satisfied_when: refuted` for a prohibition stated as its existential dual, `holds` for a universal stated directly). The query is NEVER negated internally to make the requirement read positively. A requirement carries no verdict and no status: what the models say is a query result, and what the pair MEANS is a verification (satisfied/violated/inconclusive/error), derived per read and stored nowhere. NEITHER `satisfied_when`'s vocabulary nor `expressed_as`'s join is enumerated or resolved here, for the reason `accounting`'s metric name is not: a wrong word reaches the MEANING layer, where `parseRequirement` says why `inconclusive` and `unlicensed` cannot discharge an obligation and a dangling `expressed_as` verifies as `error`, instead of JSON Schema answering SHAPE where the engine has a sentence.",
      "type": "object",
      "propertyNames": { "$ref": "#/$defs/id" },
      "additionalProperties": {
        "type": "object",
        "additionalProperties": false,
        "required": ["statement", "expressed_as", "satisfied_when"],
        "$comment": "A requirement carries no `notes:`, deliberately. The declaration enters systemHash (it is authored content that changes what the system prescribes), and a note inside it would hash with it -- which invariant A1 forbids, since annotation never alters interpretation or an analysis result. The prescription's own prose is `statement`, so nothing is lost.",
        "properties": {
          "statement": {
            "description": "The obligation in the modal voice -- \"Restricted data must not reach an impermitted subscriber.\" Prescriptive, where a query's `name` is descriptive and a model's `purpose.question` is interrogative; the three are distinct sentences and none is derived from another.",
            "type": "string",
            "minLength": 1
          },
          "expressed_as": {
            "description": "The saved query that decides this obligation, by id. A join rather than an inlined query: one query can decide several obligations, and inlining would duplicate it.",
            "$ref": "#/$defs/id"
          },
          "satisfied_when": {
            "description": "Shape only -- a string. The two-word vocabulary (`holds`, `refuted`) lives in the engine, which is also where a YAML 1.1 loader's coercion of `no` to a boolean gets the sentence citing V25.",
            "type": "string"
          }
        }
      }
    }
  },

  "$defs": {
    "endpointTypes": {
      "description": "One entity-type name, or a list meaning membership in the union. An empty authored list declares no kind and is a V47 finding, not a legal 'nothing may sit here' -- that assertion is absence: prose.",
      "anyOf": [
        { "type": "string" },
        { "type": "array", "items": { "type": "string" } }
      ]
    },
    "id": {
      "type": "string",
      "description": "Shape only. Ids that a YAML 1.1 loader would coerce (on/off/yes/no/true/false/null/~, or numeric-looking) are additionally REFUSED by the validator (V25) -- this pattern cannot catch them because the loader mangles them first.",
      "pattern": "^[A-Za-z_][A-Za-z0-9_.-]*$",
      "minLength": 1,
      "maxLength": 128
    },
    "quantity": {
      "description": "One quantitative annotation. Shape only. NOTE what is deliberately NOT constrained here: `dimension` carries no enum and `target` carries no prefix pattern, so an unrecognised dimension or target kind reaches the MEANING layer and both implementations cite the same rule (V28/V27) instead of Python answering SCHEMA where TypeScript answers a V-number. Nor is value-or-range required: V28's message tells an author what to write, which a oneOf complaint does not.",
      "type": "object",
      "required": ["target", "dimension"],
      "additionalProperties": false,
      "properties": {
        "target": {
          "description": "`<kind>:<ref>` over the closed kind set transition | relation | entity | state | parameter | model (V27). A transition is addressed `transition:<machine>#<index>` -- it has no id, and label: carries no semantics (V1). A relation is addressed by its own id:.",
          "type": "string"
        },
        "dimension": {
          "description": "One of duration | memory | cost | ratio | count. Checked at the meaning layer (V28), not here.",
          "type": "string"
        },
        "value": {
          "description": "A unit-bearing literal (`250 ms`), a bare number for the dimensionless dimensions ratio and count, or an expression. A unit-bearing literal is a STRING in YAML, which is why its magnitude is parsed by the workbench rather than by a loader: the two loaders this project runs disagree on `017`, `1_000`, `1e3` and `1:30`.",
          "oneOf": [
            { "type": "string" },
            { "type": "number" },
            {
              "type": "object",
              "required": ["expression"],
              "additionalProperties": false,
              "properties": {
                "expression": {
                  "description": "A sum of products over literals, `metrics.*` values and other quantity ids. Operators stand alone between spaces, because an id may contain `-`. No parentheses in v0.1.",
                  "type": "string"
                }
              }
            }
          ]
        },
        "range": {
          "description": "A closed interval, low then high (V29). Both ends carry the same dimension (V30).",
          "type": "array",
          "minItems": 2,
          "maxItems": 2,
          "items": { "oneOf": [{ "type": "string" }, { "type": "number" }] }
        },
        "residency": {
          "description": "`resident` -- charged in every configuration where the target exists. Exactly one of residency / when on a configuration-scoped (memory) quantity; neither is a finding, because the quantity would then enter no summand of memory(c) (V37). NO enum here deliberately: `residency: transient` is a plausible author guess and V37's message names the closed vocabulary, which a schema complaint does not.",
          "type": "string"
        },
        "when": {
          "description": "`{ state: <machine>.<state> }` -- charged exactly while that behavioral state is active. The state reference resolves like any other (V27). Left OPEN rather than closed to `state` only, for the reason a note object is left open: an unexpected key here means the charge is declared nowhere, and V37 can say that where a shape complaint cannot.",
          "type": "object"
        },
        "description": { "type": "string" },
        "notes": { "$ref": "#/$defs/notes" },
        "provenance": { "$ref": "#/$defs/provenance" }
      }
    },
    "intRange": {
      "type": "array",
      "minItems": 2,
      "maxItems": 2,
      "items": { "type": "integer" }
    },
    "scalar": {
      "oneOf": [
        { "type": "string" },
        { "type": "boolean" },
        { "type": "integer" }
      ]
    },
    "propertyBag": {
      "type": "object",
      "additionalProperties": {
        "oneOf": [
          { "$ref": "#/$defs/scalar" },
          {
            "type": "object",
            "required": ["value"],
            "additionalProperties": false,
            "properties": {
              "value": { "$ref": "#/$defs/scalar" },
              "domain": {
                "description": "Names a domain in domains:. Required for order comparisons (V20).",
                "$ref": "#/$defs/id"
              }
            }
          }
        ]
      }
    },
    "variable": {
      "type": "object",
      "required": ["type"],
      "additionalProperties": false,
      "properties": {
        "type": {
          "description": "ref is RESERVED and must be rejected with a future-version message, never misinterpreted (V15).",
          "enum": ["boolean", "integer", "enum", "ref"]
        },
        "range": { "$ref": "#/$defs/intRange" },
        "values": { "type": "array", "minItems": 1, "items": { "type": "string" } },
        "domain": { "$ref": "#/$defs/id" },
        "initial": { "$ref": "#/$defs/scalar" },
        "target": { "$ref": "#/$defs/id" },
        "nullable": { "type": "boolean" },
        "description": { "type": "string" }
      },
      "allOf": [
        {
          "if": { "properties": { "type": { "const": "integer" } } },
          "then": {
            "anyOf": [{ "required": ["range"] }, { "required": ["domain"] }],
            "$comment": "V17: finite domain mandatory."
          }
        },
        {
          "if": { "properties": { "type": { "const": "enum" } } },
          "then": { "anyOf": [{ "required": ["values"] }, { "required": ["domain"] }] }
        },
        {
          "if": { "properties": { "type": { "const": "ref" } } },
          "then": { "required": ["target"] }
        }
      ]
    },
    "transition": {
      "type": "object",
      "required": ["from", "to"],
      "additionalProperties": false,
      "properties": {
        "from": { "$ref": "#/$defs/id" },
        "to": { "$ref": "#/$defs/id" },
        "label": {
          "description": "Documentation only. Carries NO semantics -- use sync: for synchronization (V1).",
          "type": "string"
        },
        "sync": {
          "description": "Synchronization, and nothing else. MUST resolve to an entry in events: (V1).",
          "$ref": "#/$defs/id"
        },
        "requires": {
          "description": "Guard over the PRE-transition system state, evaluated atomically with the transition. A guard is NOT synchronization: it reads another machine without making it step. Refused against a multiply-instantiated machine (V11).",
          "type": "object",
          "additionalProperties": {
            "oneOf": [
              { "$ref": "#/$defs/scalar" },
              {
                "type": "object",
                "additionalProperties": false,
                "minProperties": 1,
                "properties": {
                  "eq": { "$ref": "#/$defs/scalar" },
                  "ne": { "$ref": "#/$defs/scalar" },
                  "lt": { "type": "integer" },
                  "le": { "type": "integer" },
                  "gt": { "type": "integer" },
                  "ge": { "type": "integer" }
                }
              }
            ]
          }
        },
        "effects": {
          "description": "Variable assignments. Two transitions joined by one event MUST NOT assign the same variable (V13) -- statically checked from this block.",
          "type": "object",
          "additionalProperties": { "type": "string" }
        }
      }
    },
    "purpose": {
      "description": "What this reduction is FOR. omits is CHECKED against the model's actual vocabulary, not merely asserted (V24).",
      "type": "object",
      "additionalProperties": false,
      "properties": {
        "question": { "type": "string" },
        "represents": { "type": "array", "items": { "type": "string" } },
        "omits": { "type": "array", "items": { "type": "string" } }
      }
    },
    "notes": {
      "description": "Free annotation attached to an entity, a model or a relation. NON-SEMANTIC: notes never alter interpretation or an analysis result, and the canonical hash excludes them (A1, SEMANTICS.md 5.1).",
      "type": "array",
      "items": { "$ref": "#/$defs/note" }
    },
    "note": {
      "description": "One note. OPEN on purpose: an unquoted comma in YAML flow style truncates the text and leaves a stray KEY behind, and the validator reports that under ANNOTATION with the cause named. A shape complaint about an unexpected property would be the same unhelpful message V25 exists to replace.",
      "type": "object",
      "required": ["kind", "text"],
      "additionalProperties": true,
      "properties": {
        "id": { "$ref": "#/$defs/id" },
        "kind": {
          "description": "assumption is the interesting one: a note SAYING something is an assumption does not make it part of formal analysis. To constrain a query it must be represented as a property, variable, guard or quantity.",
          "enum": ["comment", "rationale", "assumption", "question", "todo"]
        },
        "text": { "type": "string" },
        "author": {
          "description": "Which side wrote it -- human or agent. Not an identity claim.",
          "type": "string"
        },
        "at": {
          "description": "Timestamp, QUOTED. JSON has no date type and a bare one is coerced by a YAML 1.1 loader (10.1).",
          "type": "string"
        }
      }
    },
    "historyEntry": {
      "description": "One entry of a provenance history. Append-only and system-managed, never editable commentary.",
      "type": "object",
      "additionalProperties": false,
      "properties": {
        "revision": { "type": "string" },
        "actor": { "type": "string" },
        "prompt": { "type": "string" },
        "action": { "type": "string" },
        "at": { "type": "string" }
      }
    },
    "provenance": {
      "description": "Where an object came from, in both senses one key has to carry: what part of the world it corresponds to (subject / correspondence, 8.1), and who authored it (created_by / prompt / history, 5.1). Non-semantic like notes.",
      "type": "object",
      "additionalProperties": false,
      "properties": {
        "created_by": {
          "description": "human or agent.",
          "type": "string"
        },
        "created_at": { "type": "string" },
        "prompt": {
          "description": "The instruction that produced this object -- the field that earns provenance its place. With agent-authored models it answers why the model has this shape, which reading the model cannot tell you. Deliberately not the whole conversation.",
          "type": "string"
        },
        "rationale": { "type": "string" },
        "history": {
          "type": "array",
          "items": { "$ref": "#/$defs/historyEntry" }
        },
        "subject": {
          "type": "object",
          "required": ["kind"],
          "additionalProperties": false,
          "properties": {
            "kind": { "enum": ["repository", "service", "document", "external", "other"] },
            "ref": { "type": "string" },
            "description": { "type": "string" }
          }
        },
        "correspondence": {
          "type": "object",
          "required": ["kind"],
          "additionalProperties": false,
          "properties": {
            "kind": {
              "description": "How the model-to-world correspondence was established. v0.1 permits asserted; the stronger kinds are where later tooling earns trust rather than claiming it.",
              "enum": ["asserted", "checked", "derived", "generated"]
            },
            "checked": { "type": "string", "format": "date" },
            "note": { "type": "string" }
          }
        }
      }
    }
  }
}
